import { isAccountsOnly, type Notification } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { closeAnimated } from '../lib/motion';
import { useSession } from '../lib/session';

/** Where on a deal a notification is about: the page scrolls there and highlights it (see AppShell). */
const placeOf = (kind: string): string =>
  /submittal/.test(kind) ? '#submittals'
    : /brand|costing/.test(kind) ? '#qb-title'
    : /gp|approval/.test(kind) ? '#engine-title'
    : /presales|handoff|quotation|document|clarification|assign/.test(kind) ? '#docs-title'
    : '';

/** Bell with unread count; polls every minute while the tab is open. */
export function NotificationsBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { permissions } = useSession();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  // The list lifts away when closed (not when a notification is opened: then the page changes anyway).
  const hide = () => closeAnimated(box.current?.querySelector('.bell-pop'), () => setOpen(false));
  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<{ items: Notification[]; unread: number }>('/api/notifications'),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
  const read = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) => api('/api/notifications/read', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  useEffect(() => {
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) hide(); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const unread = q.data?.unread ?? 0;
  const go = (n: Notification) => {
    if (!n.readAt) read.mutate({ ids: [n.id] });
    setOpen(false);
    if (n.objectName === 'delivery_project' && n.recordId) navigate(`/projects/${n.recordId}`);
    else if (n.objectName === 'delivery_po' && n.recordId) navigate(`/projects/po/${n.recordId}`);
    else if (n.objectName === 'delivery_priority' && n.recordId) navigate(`/projects/priority/${n.recordId}`);
    else if (n.objectName === 'delivery_vo' && n.recordId) navigate(`/projects/vo/${n.recordId}`);
    else if (n.objectName === 'tool') navigate(`/projects/tools${n.recordId ? `?tool=${n.recordId}` : ''}`);
    else if (n.objectName === 'exception') navigate(`/exceptions${n.recordId ? `?open=${n.recordId}` : ''}`);
    else if (n.objectName === 'supplier') navigate(`/purchasing?tab=suppliers${n.recordId ? `&supplier=${n.recordId}` : ''}`);
    // A task from the weekly sales meeting: Home shows it under My tasks.
    else if (n.objectName === 'my_tasks') navigate('/');
    else if (n.objectName === 'expense_claim') navigate(`/expenses${n.recordId ? `?claim=${n.recordId}` : ''}`);
    else if (n.objectName === 'purchase_request') navigate(`/purchasing${n.recordId ? `?open=${n.recordId}` : ''}`);
    else if (n.objectName === 'workforce') navigate(`/projects/manpower${n.recordId ? `?worker=${n.recordId}` : ''}`);
    else if (n.objectName === 'shipment') navigate(`/logistics${n.recordId ? `?show=all&shipment=${n.recordId}` : ''}`);
    // The Accounts tab (the home page of accounts-only users).
    else if (n.objectName === 'accounts_center') navigate(isAccountsOnly(permissions) ? '/' : '/accounts');
    else if (n.objectName && n.recordId) navigate(`/o/${n.objectName}/${n.recordId}${n.objectName === 'opportunity' ? placeOf(n.kind) : ''}`);
  };

  return (
    <div className="bell" ref={box}>
      <button className="icon-btn" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} aria-expanded={open} onClick={() => (open ? hide() : setOpen(true))}>
        <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
          <path d="M5 13.5V9a5 5 0 0 1 10 0v4.5l1.5 2h-13zM8.2 17.5a2 2 0 0 0 3.6 0" />
        </svg>
        {unread > 0 && <span className="badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="bell-pop" role="dialog" aria-label="Notifications">
          <header>
            <b>Notifications</b>
            {unread > 0 && <button className="btn ghost small" onClick={() => read.mutate({ all: true })}>Mark all read</button>}
          </header>
          {q.data?.items.length ? (
            <ul>
              {q.data.items.map((n) => (
                <li key={n.id} className={n.readAt ? '' : 'unread'}>
                  <button onClick={() => go(n)}>
                    <b>{n.title}</b>
                    {n.body && <span className="muted small">{n.body}</span>}
                    <span className="muted small">{n.actorName ? `${n.actorName} · ` : ''}{formatDateTime(n.createdAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted pad">You're all caught up.</p>
          )}
        </div>
      )}
    </div>
  );
}
